import React, { useState, useEffect, useCallback } from "react";
import {
    Stack,
    Heading,
    Button,
    Card,
    Text,
    Modal,
    Alert,
} from "@dgraph-io/typhoon-ui";
import { observer } from "mobx-react-lite";
import { useForm } from "@dgraph-io/typhoon-ui";
import { useUpdateBackend } from "./utils";
//@ts-ignore
import AppLink from "components/shared/AppLink/AppLink";
import { DeploymentStore } from "store/deployments/deployments";
import LoadingSpinner from "components/shared/LoadingSpinner/LoadingSpinner";
import deploymentStore from "store/deployments/deployments";
import { CostCalculator } from "components/pages/PricingCalculator/PriceBreakup";
import { noop } from "@dgraph-io/typhoon-ui";
import {
    disabledTiersGCP,
    getTierByID,
    Tier,
    tiers,
} from "components/pages/PricingCalculator/ProviderData";
import { PageContentContainerStyles } from "../styles";
import { DeploymentTypeSelect } from "components/shared/DeploymentTypeSelect/DeploymentTypeSelect";
import { EnterpriseSettings } from "components/shared/EnterpriseSettings/EnterpriseSettings";
import { SlashPricingCard } from "components/shared/SlashPricingCard/SlashPricingCard";
import config, { useAppRoute } from "config";
import { DeploymentType } from "store/deployments/deployments.types";
import trackEvent, { events, trackPage } from "libs/trackevents";
import { useAddCreditCard } from "hooks/useAddCreditCard/useAddCreditCard";
import { Elements, StripeProvider } from "react-stripe-elements";
import AddCard from "components/shared/AddCard/AddCard";
import { getJaegerTierByID } from "components/shared/JaegerConfigSelection/constants";
import { useContactFormForDgraphCloud } from "hooks/useContactFormForDgraphCloud/useContactFormForDgraphCloud";
import { ContactForm } from "components/shared/ContactForm/ContactForm";
import { useAuth } from "libs/auth";

const ModifyBackendPage = ({
    deploymentStore,
}: {
    deploymentStore: DeploymentStore;
}) => {
    const { path } = useAppRoute();
    const { user } = useAuth();
    const deployment = deploymentStore.currentDeployment!;

    const isUserOnPaidPlan =
        deploymentStore.currentDeployment?.organization !== null
            ? deploymentStore.currentDeployment?.organization.createdBy
                  ?.accountType === "paid"
            : user?.accountType === "paid";

    const isEnterprise = deployment.deploymentType === "dedicated";
    const [deploymentType, setDeploymentType] = useState<DeploymentType>(
        deployment.deploymentType
    );
    const [patchAttributes, setPatchAttributes] = useState([]);
    const isOwner = deployment.owner === user?.authId;

    const { form, setValue: setEnterpriseSettingsForm } = useForm({
        cloudProvider: {
            default: {
                key: deployment.provider,
                icon: null,
                name: deployment.provider,
                zoneProvider: [],
            },
        },
        dgraphHosted: {
            default: true,
        },
        dgraphHA: {
            default: deployment.dgraphHA,
        },
        storage: {
            default: deployment.alphaStorage,
        },
        selectedTier: {
            default: (getTierByID(deployment.size) || tiers[0]) as Tier,
        },
        zone: {
            default: deployment.zone,
        },
        acls: {
            default: deployment.aclEnabled,
        },
        alphaNodes: {
            default: null as null | number,
        },
        auditlogs: {
            default: deployment.isAuditEnabled || false,
        },
        zeroNodes: {
            default: null as null | number,
        },
        jaeger: {
            default: deployment.jaegerEnabled
                ? getJaegerTierByID(`${deployment.jaegerSize}C`)
                : null,
        },
        jaegerTrace: {
            default: deployment.jaegerTrace,
        },
        shardCount: {
            default: deployment.shardCount || 1,
        },
        prometheus: {
            default:
                deployment.prometheusEnabled === true &&
                deployment.prometheusSize
                    ? deployment.prometheusSize
                    : null,
        },
    });

    const {
        isModalOpen: isContactModalOpen,
        setIsModalOpen: setIsContactModalOpen,
        submitForm: submitContactForm,
        contactForm,
        contactFormErrors,
    } = useContactFormForDgraphCloud();

    const {
        acls,
        jaeger,
        dgraphHA,
        selectedTier,
        jaegerTrace,
        auditlogs,
        shardCount,
        prometheus,
    } = form;

    const isPrometheusValueDifferent =
        deployment.prometheusEnabled === true
            ? prometheus.value === null ||
              deployment.prometheusSize !== prometheus.value
            : prometheus.value !== null;

    const [loading, setLoading] = useState(false);
    const {
        onMoveToPaidPlan,
        isCardModalOpen,
        setIsCardModalOpen,
        isLoading: addCreditCardLoading,
    } = useAddCreditCard();

    const isJaegerEnabledOnPage = jaeger.value !== null;

    const updateBackend = useUpdateBackend(deployment, {
        ...(deployment.deploymentType !== deploymentType
            ? {
                  deploymentType: deploymentType,
              }
            : {}),
        ...(isEnterprise
            ? {
                  ...(deployment.aclEnabled !== acls.value
                      ? { aclEnabled: String(acls.value) }
                      : {}),
                  ...(deployment.dgraphHA !== dgraphHA.value
                      ? { dgraphHA: String(dgraphHA.value) }
                      : {}),
                  ...(deployment.isAuditEnabled !== auditlogs.value
                      ? { isAuditEnabled: auditlogs.value }
                      : {}),
                  ...((deployment.shardCount || 1) !== shardCount.value
                      ? { shardCount: shardCount.value }
                      : {}),

                  ...(jaeger.value !== null &&
                  deployment.jaegerSize !== jaeger.value?.nCPU
                      ? {
                            jaegerSize: jaeger.value?.nCPU,
                        }
                      : {}),
                  ...(jaeger.value !== null &&
                  deployment.jaegerTrace !== jaegerTrace.value
                      ? {
                            jaegerTrace: jaegerTrace.value,
                        }
                      : {}),
                  ...(deployment.jaegerEnabled !== isJaegerEnabledOnPage
                      ? {
                            jaegerEnabled: String(isJaegerEnabledOnPage),

                            ...(isJaegerEnabledOnPage
                                ? {
                                      jaegerSize: jaeger.value?.nCPU,
                                      jaegerTrace: jaegerTrace.value,
                                  }
                                : {
                                      jaegerSize: null,
                                      jaegerTrace: null,
                                  }),
                        }
                      : {}),
                  ...(deployment.size !== selectedTier.value.id
                      ? { size: selectedTier.value.id }
                      : {}),
                  ...(isPrometheusValueDifferent
                      ? {
                            prometheusSize: prometheus.value,
                            prometheusEnabled: prometheus.value !== null,
                        }
                      : {}),
              }
            : {}),
    });

    const loadPatchAttributes = useCallback(async () => {
        await deploymentStore.loadPatchAttributes(deployment?.uid);
        setPatchAttributes(deploymentStore.patchAttributes);
    }, [deploymentStore, deployment.uid]);

    useEffect(() => {
        loadPatchAttributes();
    }, [loadPatchAttributes]);

    const updateDeployment = async () => {
        trackEvent(events.modifyBackendUpdate);
        setLoading(true);
        await updateBackend();
        setLoading(false);
    };

    const onLaunchClick = () => {
        if (!isUserOnPaidPlan && deploymentType !== "free") {
            setIsCardModalOpen(true);
        } else {
            updateDeployment();
        }
    };

    useEffect(trackPage, []);

    const attr: Record<string, any> = {};

    patchAttributes &&
        patchAttributes.map((attribute: any) => {
            attr[attribute.name] = attribute.options;
        });

    const deploymentJaegerTier = deployment.jaegerEnabled
        ? getJaegerTierByID(`${deployment.jaegerSize}C`)
        : null;

    const areFormValuesSame =
        deploymentType === "dedicated"
            ? deployment.size === selectedTier.value.id &&
              deployment.aclEnabled === acls.value &&
              deployment.dgraphHA === dgraphHA.value &&
              deployment.jaegerTrace === jaegerTrace.value &&
              deploymentJaegerTier?.id === jaeger.value?.id &&
              deployment.isAuditEnabled === auditlogs.value &&
              deployment.deploymentType === deploymentType &&
              (deployment.shardCount || 1) === shardCount.value &&
              !isPrometheusValueDifferent
            : deployment.deploymentType === deploymentType;

    const isLaunchAvailable =
        deployment.provider === "gcp"
            ? !disabledTiersGCP.includes(selectedTier.value.id)
            : true;

    return (
        <Stack direction="vertical" styles={PageContentContainerStyles} gap={3}>
            <Stack direction="vertical">
                <AppLink to={path + "/settings"}>
                    <Text>Settings</Text>
                </AppLink>
                <Heading variant={1} weight={300}>
                    Modify Backend
                </Heading>
            </Stack>

            {!isOwner && (
                <Alert variant="info">
                    Only organization admin can modify settings of the backend
                </Alert>
            )}

            {isOwner && (
                <LoadingSpinner loading={!patchAttributes.length}>
                    <Stack direction="horizontal" gap={2} fullWidth={true}>
                        <Card styles={{ flex: 1, height: "fit-content" }}>
                            <Stack direction="vertical" gap={2}>
                                <Stack direction="vertical" gap={1}>
                                    <Text variant={3} color="grey">
                                        Select Backend Type
                                    </Text>
                                    <DeploymentTypeSelect
                                        deploymentType={deploymentType}
                                        onChange={setDeploymentType}
                                        disabled={
                                            deploymentType === "dedicated"
                                                ? {
                                                      dedicated: false,
                                                      shared: true,
                                                      free: true,
                                                  }
                                                : {
                                                      dedicated: true,
                                                      shared: false,
                                                      free: !deploymentStore.isFreeBackendOptionAvailable(),
                                                  }
                                        }
                                    />
                                </Stack>

                                <>
                                    <LoadingSpinner loading={loading}>
                                        <Stack direction="vertical" gap={2}>
                                            {isEnterprise && (
                                                <EnterpriseSettings
                                                    settingsForm={form}
                                                    setSettingsForm={
                                                        setEnterpriseSettingsForm
                                                    }
                                                    disableHAToggle={
                                                        deployment.dgraphHA ===
                                                        true
                                                    }
                                                    disableStorage={true}
                                                    minShardCount={
                                                        deployment.shardCount ||
                                                        1
                                                    }
                                                />
                                            )}
                                            {isLaunchAvailable && (
                                                <Stack
                                                    direction="vertical"
                                                    align="flex-end"
                                                    gap={1}
                                                >
                                                    <Button
                                                        disabled={
                                                            areFormValuesSame ||
                                                            addCreditCardLoading
                                                        }
                                                        onClick={onLaunchClick}
                                                    >
                                                        Launch
                                                    </Button>
                                                    <Text color="grey">
                                                        * This action might
                                                        result in a backend
                                                        restart.
                                                    </Text>
                                                </Stack>
                                            )}
                                            {!isLaunchAvailable && (
                                                <Button
                                                    styles={{
                                                        width: "fit-content",
                                                        marginLeft: "auto",
                                                    }}
                                                    onClick={() =>
                                                        setIsContactModalOpen(
                                                            true
                                                        )
                                                    }
                                                >
                                                    Contact Us
                                                </Button>
                                            )}
                                        </Stack>
                                    </LoadingSpinner>
                                </>
                            </Stack>
                        </Card>
                        <Card
                            styles={{
                                width: "350px",
                                height: "fit-content",
                                padding: 0,
                            }}
                        >
                            {deploymentType === "dedicated" ? (
                                <CostCalculator
                                    form={form}
                                    setDeploymentCostPerHour={noop}
                                />
                            ) : (
                                <SlashPricingCard
                                    isPaid={deploymentType === "shared"}
                                    region={deployment.zone}
                                />
                            )}
                        </Card>
                    </Stack>
                </LoadingSpinner>
            )}
            <Modal
                isOpen={isCardModalOpen}
                onClose={() => setIsCardModalOpen(false)}
            >
                <StripeProvider apiKey={config.stripe.publicKey}>
                    <Elements>
                        <AddCard
                            onSuccess={() => {
                                setIsCardModalOpen(false);
                                updateDeployment();
                            }}
                            onFailure={() => setIsCardModalOpen(false)}
                            onCreateSourceID={onMoveToPaidPlan}
                            onCancel={() => setIsCardModalOpen(false)}
                        />
                    </Elements>
                </StripeProvider>
            </Modal>
            <Modal
                styles={{ position: "fixed" }}
                onClose={() => setIsContactModalOpen(false)}
                isOpen={isContactModalOpen}
            >
                <ContactForm
                    form={contactForm}
                    formErrors={contactFormErrors}
                    onSubmit={() => submitContactForm(form)}
                />
            </Modal>
        </Stack>
    );
};

export default observer(() => (
    <ModifyBackendPage
        deploymentStore={deploymentStore}
        key={deploymentStore.currentDeployment?.uid || 0}
    />
));
